import { alpha, useTheme } from '@rossum/ui/material';
import { bboxColorMap } from '../../annotation-view/document-canvas-svg/bounding-box-layer/useBoundingBoxes';
import { FlatDatapoint } from '../core';

type FieldHighlightOverlayProps = {
  datapoints: Array<FlatDatapoint>;
  pageWidth: number;
  pageHeight: number;
  focusedSchemaId: string | null;
};

/**
 * SVG overlay that renders bounding box highlights for annotation fields.
 * Positioned absolutely over the page preview image.
 * Uses viewBox matching page dimensions so coordinates map 1:1.
 */
export const FieldHighlightOverlay = ({
  datapoints,
  pageWidth,
  pageHeight,
  focusedSchemaId,
}: FieldHighlightOverlayProps) => {
  const theme = useTheme();

  if (datapoints.length === 0 || !pageWidth || !pageHeight) return null;

  return (
    <svg
      viewBox={`0 0 ${pageWidth} ${pageHeight}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      {datapoints.map(dp => {
        const [x1, y1, x2, y2] = dp.position;
        const isFocused =
          focusedSchemaId !== null && dp.schemaId === focusedSchemaId;
        const fill = alpha(
          dp.validationSources.includes('human')
            ? bboxColorMap.success
            : bboxColorMap.warning,
          0.25
        );
        const stroke = isFocused ? theme.palette.primary.main : 'transparent';

        return (
          <rect
            key={`${dp.schemaId}-${dp.position.join('-')}`}
            x={x1}
            y={y1}
            width={x2 - x1}
            height={y2 - y1}
            fill={fill}
            stroke={stroke}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
            rx={2}
            ry={2}
          />
        );
      })}
    </svg>
  );
};
